/**
 * User Management Service
 * Handles ClientAdmin user management operations (status, role, deletion)
 */

import { API_ENDPOINTS } from '../config/api';
import { makeAuthenticatedRequest, createApiError } from './apiService';

export interface UserStatusChangeResponse {
  userId: string;
  email: string;
  isActive: boolean;
  message: string;
}

export interface UserRoleChangeResponse {
  userId: string;
  email: string;
  oldRole: string;
  newRole: string;
  message: string;
}

export interface UserDeletionResponse {
  userId: string;
  email: string;
  markedForDeletionAt?: string;
  permanentDeletionDate?: string;
  message: string;
}

/**
 * Toggle user active status
 */
export const updateUserStatus = async (
  userId: string,
  isActive: boolean
): Promise<UserStatusChangeResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.USER_MANAGEMENT.UPDATE_STATUS(userId),
    {
      method: 'PUT',
      body: JSON.stringify({ isActive }),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Change user role
 */
export const updateUserRole = async (
  userId: string,
  newRole: 'ClientUser' | 'ClientAdmin'
): Promise<UserRoleChangeResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.USER_MANAGEMENT.UPDATE_ROLE(userId),
    {
      method: 'PUT',
      body: JSON.stringify({ role: newRole }),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Mark user for deletion (30-day grace period)
 */
export const markUserForDeletion = async (userId: string): Promise<UserDeletionResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.USER_MANAGEMENT.DELETE(userId),
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Restore user from deletion
 */
export const restoreUser = async (userId: string): Promise<UserDeletionResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.USER_MANAGEMENT.RESTORE(userId),
    {
      method: 'POST',
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

export interface UserPermissions {
  canAccessNmvtis: boolean;
  canAccessTheftLien: boolean;
  canAccessMotorVehicleRecord: boolean;
  canAccessVinDecoder: boolean;
  canAccessAstsPublic: boolean;
  canAccessInvoicing?: boolean;
  canManageClients?: boolean;
  mfaEnabled?: boolean;
  receivePortalAlerts?: boolean;
}

export interface UserPermissionsChangeResponse {
  userId: string;
  canAccessNmvtis: boolean;
  canAccessTheftLien: boolean;
  canAccessMotorVehicleRecord: boolean;
  canAccessVinDecoder: boolean;
  canAccessAstsPublic: boolean;
  canAccessInvoicing?: boolean;
  canManageClients?: boolean;
  receivePortalAlerts?: boolean;
  message: string;
}

/**
 * Update user section permissions
 */
export const updateUserPermissions = async (
  userId: string,
  permissions: UserPermissions
): Promise<UserPermissionsChangeResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.USER_MANAGEMENT.UPDATE_PERMISSIONS(userId),
    {
      method: 'PUT',
      body: JSON.stringify(permissions),
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};

/**
 * Delete user immediately (marked deleted, not removed)
 */
export const forceDeleteUser = async (userId: string): Promise<UserDeletionResponse> => {
  const response = await makeAuthenticatedRequest(
    API_ENDPOINTS.USER_MANAGEMENT.FORCE_DELETE(userId),
    {
      method: 'DELETE',
    }
  );

  if (!response.ok) {
    const error = await createApiError(response);
    throw error;
  }

  return await response.json();
};
